import * as React from 'react';
import * as R from 'ramda';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@common/components/ui/accordion';
import { Button } from '../../../../common/components/button';
import SearchBar from '../../../../common/components/search-bar';
import PureList from '../../../../common/components/list';
import Icon from '../../../../common/components/icon';
import ChannelItem from '../channels/item';
import { ETimelineTypes } from '../../definitions';
import { EChannelVisibility, EPermissions } from '@common/definitions';
import { usePermission } from '@common/hooks';
import type { Network, Team, Organisation } from '../../../../common/types/objects';

const {
 useMemo, useState, useEffect, useCallback, useRef
} = React;

interface ExtendedTeam extends Team {
  isGeneral?: boolean;
  networkData?: Network;
  networkName?: string;
}

interface NetworkFilterPanelProps {
  search: string;
  showMoreNetworks: boolean;
  networks: Array<Network>;
  organisationTeams: Array<Team>;
  channels: Array<any>;
  organisation: Organisation;
  t: (key: string, values?: Object) => string;
  onSearch: (query?: string) => void;
  onSelectTimeline: (type: string, id: string, parent: any, networkId?: string) => void;
  onToggleShowMore: () => void;
}

function NetworkFilterPanel({
  search,
  showMoreNetworks,
  networks,
  organisationTeams,
  channels,
  organisation,
  t,
  onSearch,
  onSelectTimeline,
  onToggleShowMore,
}: NetworkFilterPanelProps) {
  const [openAccordion, setOpenAccordion] = useState('');
  const canViewAllNetworks = usePermission(EPermissions.ORGANISATION_NETWORKS_VIEW_ALL);
  const lastAutoOpenedNetworkIdRef = useRef<string | null>(null);

  const filterChannels = useMemo(() => {
    return (
      channels?.filter((channel) => {
        const isPrivate = channel.visibility === EChannelVisibility.PRIVATE;
        const isPublicOrApproval =
          channel.visibility === EChannelVisibility.PUBLIC ||
          channel.visibility === EChannelVisibility.PUBLIC_WITH_APPROVAL;

        const isMemberOfVisibleChannel =
          channel.is_member && isPublicOrApproval;

        const isVisible = isPrivate || isMemberOfVisibleChannel;
        return isVisible && (search ? channel.name.toLowerCase().includes(search.toLowerCase()) : true);
      }) ?? []
    );
  }, [channels, search]);

  const networksCount = useMemo(() => {
    return networks?.filter((n) => canViewAllNetworks || n.membership).length;
  }, [networks, canViewAllNetworks]);

  const filteredNetworks = useMemo(() => {
    return networks
      .filter((n) => canViewAllNetworks || n.membership)
      .filter((n) => !search || n.name.toLowerCase()
        .includes(search.toLowerCase()))
      .map((n, index) => ({ ...n, originalIndex: index }))
      .sort((a, b) => {
        const aHasMembership = !!a.membership;
        const bHasMembership = !!b.membership;
        if (aHasMembership !== bHasMembership) {
          return aHasMembership ? -1 : 1;
        }
        if (aHasMembership && bHasMembership) {
          return a.originalIndex - b.originalIndex;
        }
        const aStartsWithNumber = /^[0-9]/.test(a.name);
        const bStartsWithNumber = /^[0-9]/.test(b.name);
        if (aStartsWithNumber !== bStartsWithNumber) {
          return aStartsWithNumber ? -1 : 1;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })
      .map(({ originalIndex, ...n }) => n);
  }, [canViewAllNetworks, networks, search]);

  const shouldOpenNetwork = useCallback((networkData: Network) => {
    if (networksCount === 1) return true;

    if (!search || !filteredNetworks.length) {
      // When search is cleared, don't force open - let user control
      return false;
    }

    const orgTeams = organisationTeams?.filter(
      (team) => team.network_id === networkData.id
    ) || [];
    const isSearchInTeams = !!(search && orgTeams?.some(
      (team) => team.name.toLowerCase().includes(search.toLowerCase())
    ));

    return isSearchInTeams;
  }, [networksCount, organisationTeams, search, filteredNetworks.length]);

  useEffect(() => {
    const networkToOpen = filteredNetworks.find(shouldOpenNetwork);
    const networkIdToOpen = networkToOpen?.id || null;

    if (networkIdToOpen !== lastAutoOpenedNetworkIdRef.current) {
      lastAutoOpenedNetworkIdRef.current = networkIdToOpen;
      setOpenAccordion(networkIdToOpen ? `network-${networkIdToOpen}` : '');
    }
  }, [filteredNetworks, shouldOpenNetwork]);

  const filteredTeamsBySearch = useMemo(() => {
    if (!search || !search.trim() || !networks || !organisationTeams) return [];
    const lowerSearch = search?.toLowerCase() || '';

    return networks
      .filter((n) => canViewAllNetworks || n.membership)
      .filter((n) => !n.name.toLowerCase().includes(lowerSearch.toLowerCase()))
      .flatMap((n) => organisationTeams
        .filter((team) => team.network_id === n.id)
        .filter((team) => (lowerSearch
          ? n.name.toLowerCase().includes(lowerSearch) ||
                team.name.toLowerCase().includes(lowerSearch)
          : true)
        )
        .map((team) => ({ ...team, networkData: n, networkName: n.name } as ExtendedTeam))
      );
  }, [canViewAllNetworks, networks, organisationTeams, search]);

  return (
    <>
      <SearchBar
        defaultFocus
        defaultValue={search}
        debounce={false}
        filter={false}
        placeholder={t('social:post_message_search_bar_placeholder')}
        onSearch={(value) => onSearch(value as string)}
      />

      <div className="Channels tw-max-h-[500px] tw-overflow-y-auto">
        {(filteredNetworks.length > 0 || filteredTeamsBySearch.length > 0) && (
          <h5 className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-p-3.5 tw-mb-0 tw-pl-5">
            <span>{t('social:internal_communities')}</span>
          </h5>
        )}
        {filteredTeamsBySearch.length > 0 && (
          <PureList
            // @ts-expect-error
            items={filteredTeamsBySearch}
            renderRow={ChannelItem}
            // @ts-expect-error
            rowProps={(team: ExtendedTeam) => ({
              sub: true,
              item: {
                ...team,
                icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
                isSearch: !!search,
              },
              onSelectChannel: () => onSelectTimeline(
                team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM,
                team.id,
                team,
                team.networkData?.id
              ),
            })}
          />
        )}
        <Accordion type="single" value={openAccordion} onValueChange={setOpenAccordion} collapsible>
          {filteredNetworks
            .slice(0, showMoreNetworks ? networks.length : 10)
            .map((networkData, index) => {
              const orgTeams = organisationTeams?.filter(
                (team) => team.network_id === networkData.id
              ) || [];
              const teamsWithGeneral: ExtendedTeam[] = orgTeams?.length > 0
                ? [
                    {
                      id: networkData.id,
                      name: t('social:timeline_header_network_general'),
                      network_id: networkData.id,
                      isGeneral: true,
                    } as ExtendedTeam,
                    ...orgTeams,
                  ]
                : [];

              if (networksCount === 1) {
                return (
                  <React.Fragment key={networkData.id}>
                    <div className="tw-flex tw-items-center tw-pr-3.5">
                      <ChannelItem
                        addClassName="!tw-flex-1 !tw-pr-3"
                        isNetwork
                        // @ts-expect-error
                        item={{
                          ...networkData,
                          icon: (
                            <Icon type="community_icon" className="active-icon !tw-w-5 !tw-h-5" />
                          ),
                        }}
                        onSelectChannel={() => onSelectTimeline(
                          ETimelineTypes.NETWORK,
                          networkData.id,
                          networkData
                        )}
                      />
                      <Icon type="expand_more" className="tw-rotate-180" />
                    </div>
                    <PureList
                      // @ts-expect-error
                      items={teamsWithGeneral}
                      renderRow={ChannelItem}
                      // @ts-expect-error
                      rowProps={(team: ExtendedTeam) => ({
                        sub: true,
                        item: {
                          ...team,
                          icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
                          networkName: networkData.name,
                        },
                        onSelectChannel: () => onSelectTimeline(
                          team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM,
                          team.id,
                          team,
                          networkData.id
                        ),
                      })}
                    />
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={networkData.id}>
                  {teamsWithGeneral.length > 0 ? (
                    <AccordionItem
                      value={`network-${networkData.id}`}
                      className="community-accordion-item post-message-accordion-item"
                    >
                      <AccordionTrigger
                        className="tw-p-0 hover:tw-no-underline tw-text-left tw-cursor-pointer"
                        icon={<Icon type="expand_more" />}
                        iconClassName="post-message-accordion-item__chevron"
                      >
                        <ChannelItem
                          addClassName="!tw-flex-1"
                          isNetwork
                          // @ts-expect-error
                          item={{
                            ...networkData,
                            icon: (
                              <Icon type="community_icon" className="active-icon !tw-w-5 !tw-h-5" />
                            ),
                          }}
                          onSelectChannel={() => onSelectTimeline(
                            ETimelineTypes.NETWORK,
                            networkData.id,
                            networkData
                          )}
                        />
                      </AccordionTrigger>
                      <AccordionContent>
                        <PureList
                          // @ts-expect-error
                          items={teamsWithGeneral}
                          renderRow={ChannelItem}
                          // @ts-expect-error
                          rowProps={(team: ExtendedTeam) => ({
                            sub: true,
                            item: {
                              ...team,
                              icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
                              networkName: networkData.name,
                            },
                            onSelectChannel: () => onSelectTimeline(
                              team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM,
                              team.id,
                              team,
                              networkData.id
                            ),
                          })}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ) : (
                    <div className="tw-flex tw-items-center tw-w-full">
                      <ChannelItem
                        addClassName="!tw-flex-1"
                        isNetwork
                        // @ts-expect-error
                        item={{
                          ...networkData,
                          icon: (
                            <Icon type="community_icon" className="active-icon !tw-w-5 !tw-h-5" />
                          ),
                        }}
                        onSelectChannel={() => onSelectTimeline(
                          ETimelineTypes.NETWORK,
                          networkData.id,
                          networkData
                        )}
                      />
                    </div>
                  )}
                  {filteredNetworks?.length !== index + 1 && (
                    <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-my-2" />
                  )}
                </React.Fragment>
              );
            })}
          {filteredNetworks.length > 10 && (
            <>
              <div className="tw-flex tw-justify-center">
                <Button
                  onClick={onToggleShowMore}
                  type="transparent"
                  className="!tw-py-0.5"
                >
                  {showMoreNetworks ? 'Show less' : 'Show more'}
                </Button>
              </div>
              <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-my-2" />
            </>
          )}
        </Accordion>
        {filterChannels.length > 0 && (
          <h5 className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-p-3.5 tw-mb-0 tw-pl-5">
            <span>{t('social:organisation_groups')}</span>
          </h5>
        )}
        <PureList
          // @ts-expect-error
          items={filterChannels.map((channel) => R.assoc(
            'image',
            channel.theme.logo_url || organisation.brand_icon,
            channel
          ))}
          renderRow={ChannelItem}
          rowProps={(group) => ({
            onSelectChannel: () => onSelectTimeline(ETimelineTypes.CHANNEL, group.id, group),
          })}
        />
      </div>
    </>
  );
}

export default React.memo(NetworkFilterPanel);
